import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { kisiselGiderAPI } from '../../api/api';
import './KisiselGiderler.css';
import { onayla } from '../../utils/onayla';

/**
 * Baran'in kisisel gider paneli.
 *
 * Iki ayri kayit bicimi var, cunku iki ayri ihtiyac var:
 *
 *   1. SABIT KALEMLER — her ay tekrar eden faturalar (ev kirasi, elektrik,
 *      kredi karti...). Bunlar bir kontrol listesi gibi calisir: ay basinda
 *      hepsi bostur, odedikce doldurulur. Bir kalemin bir ayda tek tutari
 *      olur; karta dokununca o tutar girilir ya da duzeltilir.
 *
 *   2. SERBEST HARCAMALAR — sabit listede olmayan, o aya ozgu harcamalar
 *      (arac bakimi, ucak bileti...). Bunlara istenildigi kadar satir
 *      eklenebilir; her biri kendi adiyla listelenir.
 *
 * "Odendi" isareti, ay icinde hangi faturanin hala bekledigini gormek icin.
 * Ustteki "Bekleyen" toplami isaretlenmemis kayitlarin toplamidir.
 */

const AY_ADLARI = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const GRUP_BASLIKLARI = {
  Ev: 'Ev Giderleri',
  Kisisel: 'Kişisel',
  Ofis: 'Ofis Giderleri',
};

/** "2026-09-16" -> ekranda "16.09.2026". Saat yazilmaz. */
const tarihGoster = (d) => {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  return String(t.getDate()).padStart(2, '0') + '.'
    + String(t.getMonth() + 1).padStart(2, '0') + '.'
    + t.getFullYear();
};

/** <input type="date"> icin "yyyy-MM-dd". */
const tarihGirdi = (d) => {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  return t.getFullYear() + '-'
    + String(t.getMonth() + 1).padStart(2, '0') + '-'
    + String(t.getDate()).padStart(2, '0');
};

/**
 * Secili ay icinde varsayilan gun. Icinde bulundugumuz aya kayit
 * giriliyorsa bugun, gecmis bir aya giriliyorsa o ayin 1'i onerilir —
 * boylece gecmis aya bugunun tarihi yazilmaz.
 */
const varsayilanGun = (yil, ay) => {
  const bugun = new Date();
  if (bugun.getFullYear() === yil && bugun.getMonth() + 1 === ay) return tarihGirdi(bugun);
  return yil + '-' + String(ay).padStart(2, '0') + '-01';
};

const paraFormat = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 })
    .format(Number(n) || 0);

const kisaPara = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000) return (v / 1000000).toFixed(1).replace('.', ',') + ' M';
  if (v >= 1000) return Math.round(v / 1000) + ' B';
  return v > 0 ? String(Math.round(v)) : '';
};

const KisiselGiderler = ({ yil, ay, hemenEkle = false }) => {
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [duzenlenen, setDuzenlenen] = useState(null);
  const [krediFormu, setKrediFormu] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState('');

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata('');
    try {
      setVeri(await kisiselGiderAPI.getAy(yil, ay));
    } catch (e) {
      setHata(e.response?.data?.message || e.message || 'Kişisel giderler yüklenemedi.');
    } finally {
      setYukleniyor(false);
    }
  }, [yil, ay]);

  useEffect(() => { yukle(); }, [yukle]);

  const kategoriler = veri?.kategoriler ?? veri?.Kategoriler ?? [];
  const giderler = veri?.giderler ?? veri?.Giderler ?? [];
  const seri = veri?.yillikSeri ?? veri?.YillikSeri ?? [];
  const krediler = veri?.krediler ?? veri?.Krediler ?? [];
  const hizliKalemler = veri?.hizliKalemler ?? veri?.HizliKalemler ?? [];
  const toplam = veri?.toplam ?? veri?.Toplam ?? 0;
  const odenen = veri?.odenenToplam ?? veri?.OdenenToplam ?? 0;
  const bekleyen = veri?.bekleyenToplam ?? veri?.BekleyenToplam ?? 0;

  const kayitlar = useMemo(() => giderler.map((g) => ({
    id: g.id ?? g.Id,
    kategori: g.kategori ?? g.Kategori,
    tutar: Number(g.tutar ?? g.Tutar) || 0,
    aciklama: g.aciklama ?? g.Aciklama ?? '',
    odendi: !!(g.odendi ?? g.Odendi),
    odemeTarihi: g.odemeTarihi ?? g.OdemeTarihi ?? null,
    krediId: g.krediId ?? g.KrediId ?? null,
  })), [giderler]);

  const sabitAdlar = useMemo(
    () => new Set(kategoriler.map((k) => k.ad ?? k.Ad)),
    [kategoriler]
  );

  /** Sabit kalem adı -> o ayki tek kaydı. */
  const sabitKayitlar = useMemo(() => {
    const m = new Map();
    for (const k of kayitlar) {
      if (sabitAdlar.has(k.kategori) && !m.has(k.kategori)) m.set(k.kategori, k);
    }
    return m;
  }, [kayitlar, sabitAdlar]);

  /** Sabit listede olmayan her şey serbest harcamadır. */
  // Kredi taksitleri kendi bolumunde gosterildigi icin bu listeye girmez.
  const serbestKayitlar = useMemo(
    () => kayitlar
      .filter((k) => !sabitAdlar.has(k.kategori) && !k.krediId)
      .sort((a, b) => {
        const ta = a.odemeTarihi ? new Date(a.odemeTarihi).getTime() : 0;
        const tb = b.odemeTarihi ? new Date(b.odemeTarihi).getTime() : 0;
        if (ta !== tb) return tb - ta;   // once en yeni harcama
        return b.tutar - a.tutar;
      }),
    [kayitlar, sabitAdlar]
  );

  const serbestToplam = serbestKayitlar.reduce((t, k) => t + k.tutar, 0);

  const gruplar = useMemo(() => {
    const g = {};
    for (const k of kategoriler) {
      const grup = k.grup ?? k.Grup;
      (g[grup] = g[grup] || []).push(k);
    }
    return g;
  }, [kategoriler]);

  const sabitKalemiAc = (kategori) => {
    const ad = kategori.ad ?? kategori.Ad;
    const sabit = kategori.sabitTutar ?? kategori.SabitTutar;
    const mevcut = sabitKayitlar.get(ad);
    setHata('');
    setDuzenlenen({
      serbest: false,
      baslik: ad,
      kategori: ad,
      id: mevcut ? mevcut.id : null,
      tutar: mevcut ? String(mevcut.tutar) : (sabit != null ? String(sabit) : ''),
      aciklama: mevcut ? mevcut.aciklama : '',
      odendi: mevcut ? mevcut.odendi : false,
      tarih: mevcut?.odemeTarihi ? tarihGirdi(mevcut.odemeTarihi) : varsayilanGun(yil, ay),
    });
  };

  /**
   * Hizli gider: adi hazir, imlec dogrudan tutara gider. Her dokunusta
   * yeni satir acilir — ayni ay icinde birden fazla market alisverisi
   * ayri ayri durur.
   */
  const hizliKalemiAc = (ad) => {
    setHata('');
    setDuzenlenen({
      serbest: true,
      hizli: true,
      baslik: ad,
      kategori: ad,
      id: null,
      tutar: '',
      aciklama: '',
      odendi: true,
      tarih: varsayilanGun(yil, ay),
    });
  };

  const serbestKaydiAc = (kayit) => {
    setHata('');
    setDuzenlenen({
      serbest: true,
      baslik: kayit ? kayit.kategori : 'Yeni Harcama',
      kategori: kayit ? kayit.kategori : '',
      id: kayit ? kayit.id : null,
      tutar: kayit ? String(kayit.tutar) : '',
      aciklama: kayit ? kayit.aciklama : '',
      odendi: kayit ? kayit.odendi : true,
      tarih: kayit?.odemeTarihi ? tarihGirdi(kayit.odemeTarihi) : varsayilanGun(yil, ay),
    });
  };


  /* Ana ekran widget'indan "Gider ekle" ile gelindiginde form
     dogrudan acilir; kullanici sayfayi arayip dugme aramaz. */
  const ekleIstendi = useRef(false);
  useEffect(() => {
    if (!hemenEkle || ekleIstendi.current) return;
    ekleIstendi.current = true;
    serbestKaydiAc(null);
  }, [hemenEkle]); // eslint-disable-line

  const kaydet = async (e) => {
    e.preventDefault();
    const ad = String(duzenlenen.kategori || '').trim();
    if (!ad) { setHata('Harcamanın adını yazın.'); return; }
    const tutar = parseFloat(String(duzenlenen.tutar).replace(',', '.'));
    if (!(tutar > 0)) { setHata('Tutar sıfırdan büyük olmalıdır.'); return; }

    setKaydediliyor(true);
    setHata('');
    try {
      await kisiselGiderAPI.kaydet({
        Id: duzenlenen.id || null,
        Kategori: ad,
        Tutar: tutar,
        Yil: yil,
        Ay: ay,
        Aciklama: duzenlenen.aciklama || null,
        Odendi: duzenlenen.odendi,
        OdemeTarihi: duzenlenen.odendi && duzenlenen.tarih ? duzenlenen.tarih : null,
      });
      setDuzenlenen(null);
      await yukle();
    } catch (err) {
      setHata(err.response?.data?.message || err.message || 'Kaydedilemedi.');
    } finally {
      setKaydediliyor(false);
    }
  };

  const krediFormuAc = (kredi) => {
    setHata('');
    const bugun = new Date();
    setKrediFormu(kredi ? {
      id: kredi.id ?? kredi.Id,
      ad: kredi.ad ?? kredi.Ad,
      toplamTutar: String(kredi.toplamTutar ?? kredi.ToplamTutar ?? ''),
      taksitSayisi: String(kredi.taksitSayisi ?? kredi.TaksitSayisi ?? ''),
      taksitTutari: String(kredi.taksitTutari ?? kredi.TaksitTutari ?? ''),
      baslangicYil: kredi.baslangicYil ?? kredi.BaslangicYil,
      baslangicAy: kredi.baslangicAy ?? kredi.BaslangicAy,
      aciklama: kredi.aciklama ?? kredi.Aciklama ?? '',
    } : {
      id: null,
      ad: '',
      toplamTutar: '',
      taksitSayisi: '',
      taksitTutari: '',
      baslangicYil: bugun.getFullYear(),
      baslangicAy: bugun.getMonth() + 1,
      aciklama: '',
    });
  };

  const krediKaydet = async (e) => {
    e.preventDefault();
    const ad = String(krediFormu.ad || '').trim();
    if (!ad) { setHata('Kredinin adını yazın.'); return; }
    const tutar = parseFloat(String(krediFormu.toplamTutar).replace(',', '.'));
    if (!(tutar > 0)) { setHata('Kredi tutarı sıfırdan büyük olmalıdır.'); return; }
    const taksit = parseInt(krediFormu.taksitSayisi, 10);
    if (!(taksit >= 1)) { setHata('Taksit sayısı en az 1 olmalıdır.'); return; }
    const elleTaksit = parseFloat(String(krediFormu.taksitTutari).replace(',', '.'));

    setKaydediliyor(true);
    setHata('');
    try {
      await kisiselGiderAPI.krediKaydet({
        Id: krediFormu.id || null,
        Ad: ad,
        ToplamTutar: tutar,
        TaksitSayisi: taksit,
        TaksitTutari: elleTaksit > 0 ? elleTaksit : null,
        BaslangicYil: Number(krediFormu.baslangicYil),
        BaslangicAy: Number(krediFormu.baslangicAy),
        Aciklama: krediFormu.aciklama || null,
      });
      setKrediFormu(null);
      await yukle();
    } catch (err) {
      setHata(err.response?.data?.message || err.message || 'Kredi kaydedilemedi.');
    } finally {
      setKaydediliyor(false);
    }
  };

  const krediSil = async () => {
    if (!krediFormu?.id) return;
    if (!await onayla(
      krediFormu.ad + ' silinsin mi?\n\nÖdenmemiş taksitleri kaldırılır; '
      + 'ödenmiş taksitler geçmiş harcama olarak kalır.')) return;
    setKaydediliyor(true);
    try {
      await kisiselGiderAPI.krediSil(krediFormu.id);
      setKrediFormu(null);
      await yukle();
    } catch (err) {
      setHata(err.response?.data?.message || err.message || 'Silinemedi.');
    } finally {
      setKaydediliyor(false);
    }
  };

  const taksitIsaretle = async (kredi, odendi) => {
    setHata('');
    try {
      await kisiselGiderAPI.krediOdeme({
        KrediId: kredi.id ?? kredi.Id,
        Yil: yil,
        Ay: ay,
        Odendi: odendi,
      });
      await yukle();
    } catch (err) {
      setHata(err.response?.data?.message || err.message || 'İşaretlenemedi.');
    }
  };

  const sil = async () => {
    if (!duzenlenen?.id) return;
    if (!await onayla(duzenlenen.baslik + ' kaydı silinsin mi?')) return;
    setKaydediliyor(true);
    try {
      await kisiselGiderAPI.sil(duzenlenen.id);
      setDuzenlenen(null);
      await yukle();
    } catch (err) {
      setHata(err.response?.data?.message || err.message || 'Silinemedi.');
    } finally {
      setKaydediliyor(false);
    }
  };

  if (yukleniyor) return <div className="kg-durum">Kişisel giderler yükleniyor…</div>;
  if (hata && !veri) return <div className="kg-durum kg-hata">{hata}</div>;

  const enBuyukAy = Math.max(1, ...seri.map((x) => Number(x.toplam ?? x.Toplam) || 0));
  const yilToplami = seri.reduce((t, x) => t + (Number(x.toplam ?? x.Toplam) || 0), 0);

  return (
    <div className="kg">
      <div className="kg-ozet">
        <div className="kg-ozet-kart">
          <span className="kg-ozet-ad">{AY_ADLARI[ay - 1]} toplamı</span>
          <strong className="kg-ozet-tutar">{paraFormat(toplam)}</strong>
          <span className="kg-ozet-alt">Girilen tüm kayıtlar</span>
        </div>
        <div className="kg-ozet-kart kg-odendi">
          <span className="kg-ozet-ad">Ödenen</span>
          <strong className="kg-ozet-tutar">{paraFormat(odenen)}</strong>
          <span className="kg-ozet-alt">"Ödendi" işaretli kayıtlar</span>
        </div>
        <div className="kg-ozet-kart kg-bekliyor">
          <span className="kg-ozet-ad">Bekleyen</span>
          <strong className="kg-ozet-tutar">{paraFormat(bekleyen)}</strong>
          <span className="kg-ozet-alt">Henüz ödenmemiş olanlar</span>
        </div>
      </div>

      {/* ---- Sabit kalemler ---- */}
      <div className="kg-bolum-basi">
        <h3>Sabit Kalemler</h3>
        <p>Her ay tekrar eden faturalar. Tutar girmek için kaleme dokunun.</p>
      </div>

      {Object.entries(GRUP_BASLIKLARI).map(([grup, baslik]) => {
        const liste = gruplar[grup] || [];
        if (!liste.length) return null;
        const grupToplam = liste.reduce(
          (t, k) => t + (sabitKayitlar.get(k.ad ?? k.Ad)?.tutar || 0), 0);
        return (
          <section key={grup} className="kg-grup">
            <header className="kg-grup-basi">
              <h4>{baslik}</h4>
              <span className="kg-grup-toplam">{paraFormat(grupToplam)}</span>
            </header>
            <div className="kg-kalemler">
              {liste.map((k) => {
                const ad = k.ad ?? k.Ad;
                const sabit = k.sabitTutar ?? k.SabitTutar;
                const kayit = sabitKayitlar.get(ad);
                const doluMu = !!kayit;
                let etiket;
                if (!doluMu) etiket = sabit != null ? 'sabit · girilmedi' : 'girilmedi';
                else if (kayit.odendi) {
                  const t = tarihGoster(kayit.odemeTarihi);
                  etiket = t ? 'ödendi · ' + t : 'ödendi';
                } else etiket = 'ödenmedi';
                return (
                  <button
                    type="button"
                    key={ad}
                    className={'kg-kalem' + (doluMu ? ' dolu' : '') + (kayit?.odendi ? ' odendi' : '')}
                    onClick={() => sabitKalemiAc(k)}
                  >
                    <span className="kg-kalem-ad">{ad}</span>
                    <span className="kg-kalem-tutar">
                      {doluMu ? paraFormat(kayit.tutar) : (sabit != null ? paraFormat(sabit) : '—')}
                    </span>
                    <span className="kg-kalem-etiket">{etiket}</span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* ---- Kredi ödemeleri ---- */}
      <section className="kg-grup kg-krediler">
        <header className="kg-grup-basi">
          <h4>Kredi Ödemelerim</h4>
          <span className="kg-grup-toplam">
            {paraFormat(krediler
              .filter((k) => (k.buAyVar ?? k.BuAyVar))
              .reduce((t, k) => t + (Number(k.taksitTutari ?? k.TaksitTutari) || 0), 0))}
          </span>
        </header>

        <p className="kg-bolum-not">
          Krediyi bir kez tanımlayın; taksidi her ay kendiliğinden düşer.
          Ödediğinizde tiki işaretleyin, o ayın gideri olarak işlenir.
        </p>

        {krediler.length > 0 && (
          <ul className="kg-kredi-liste">
            {krediler.map((k) => {
              const id = k.id ?? k.Id;
              const ad = k.ad ?? k.Ad;
              const buAyVar = k.buAyVar ?? k.BuAyVar;
              const taksitNo = k.taksitNo ?? k.TaksitNo;
              const taksitSayisi = k.taksitSayisi ?? k.TaksitSayisi;
              const taksitTutari = Number(k.taksitTutari ?? k.TaksitTutari) || 0;
              const odendi = k.odendi ?? k.Odendi;
              const odenen = k.odenenTaksitSayisi ?? k.OdenenTaksitSayisi ?? 0;
              const kalan = Number(k.kalanTutar ?? k.KalanTutar) || 0;
              const tarih = tarihGoster(k.odemeTarihi ?? k.OdemeTarihi);
              const bitisAy = k.bitisAy ?? k.BitisAy;
              const bitisYil = k.bitisYil ?? k.BitisYil;
              const yuzde = taksitSayisi > 0 ? Math.round((odenen / taksitSayisi) * 100) : 0;

              return (
                <li key={id} className={'kg-kredi' + (odendi ? ' odendi' : '') + (buAyVar ? '' : ' pasif')}>
                  <div className="kg-kredi-ust">
                    <button type="button" className="kg-kredi-ad" onClick={() => krediFormuAc(k)}>
                      {ad}
                      <span className="kg-kredi-alt">
                        {odenen}/{taksitSayisi} taksit ödendi · kalan {paraFormat(kalan)}
                      </span>
                    </button>

                    {buAyVar ? (
                      <label className="kg-kredi-tik">
                        <input
                          type="checkbox"
                          checked={!!odendi}
                          onChange={(e) => taksitIsaretle(k, e.target.checked)}
                        />
                        <span className="kg-kredi-tutar">{paraFormat(taksitTutari)}</span>
                        <span className="kg-kredi-durum">
                          {odendi
                            ? (tarih ? 'ödendi · ' + tarih : 'ödendi')
                            : taksitNo + '. taksit · ödenecek'}
                        </span>
                      </label>
                    ) : (
                      <span className="kg-kredi-yok">
                        {AY_ADLARI[bitisAy - 1]} {bitisYil}'de bitiyor
                      </span>
                    )}
                  </div>

                  <div className="kg-kredi-cubuk" aria-hidden="true">
                    <div className="kg-kredi-dolu" style={{ width: yuzde + '%' }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <button type="button" className="kg-ekle-dugme" onClick={() => krediFormuAc(null)}>
          + Kredi Ekle
        </button>
      </section>

      {/* ---- Serbest harcamalar ---- */}
      <section className="kg-grup kg-serbest">
        <header className="kg-grup-basi">
          <h4>Diğer Harcamalar</h4>
          <span className="kg-grup-toplam">{paraFormat(serbestToplam)}</span>
        </header>

        <p className="kg-bolum-not">
          Sabit listede olmayan harcamalar. İstediğiniz kadar satır ekleyebilirsiniz.
        </p>

        {hizliKalemler.length > 0 && (
          <div className="kg-hizli">
            <span className="kg-hizli-baslik">Hızlı ekle</span>
            <div className="kg-hizli-dugmeler">
              {hizliKalemler.map((h) => (
                <button
                  type="button"
                  key={h}
                  className="kg-hizli-dugme"
                  onClick={() => hizliKalemiAc(h)}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>
        )}

        {serbestKayitlar.length > 0 && (
          <ul className="kg-serbest-liste">
            {serbestKayitlar.map((k) => (
              <li key={k.id}>
                <button type="button" className="kg-serbest-satir" onClick={() => serbestKaydiAc(k)}>
                  <span className="kg-serbest-ad">
                    {k.kategori}
                    {k.aciklama && <span className="kg-serbest-aciklama">{k.aciklama}</span>}
                  </span>
                  <span className={'kg-serbest-durum' + (k.odendi ? ' odendi' : '')}>
                    {k.odendi
                      ? (tarihGoster(k.odemeTarihi) || 'ödendi')
                      : 'bekliyor'}
                  </span>
                  <span className="kg-serbest-tutar">{paraFormat(k.tutar)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <button type="button" className="kg-ekle-dugme" onClick={() => serbestKaydiAc(null)}>
          + Harcama Ekle
        </button>
      </section>

      {/* ---- Yıllık grafik ---- */}
      <section className="kg-grafik-blok">
        <header className="kg-grup-basi">
          <h4>{yil} yılı aylık seyir</h4>
          <span className="kg-grup-toplam">{paraFormat(yilToplami)}</span>
        </header>
        <div className="kg-grafik">
          {seri.map((x) => {
            const m = x.ay ?? x.Ay;
            const t = Number(x.toplam ?? x.Toplam) || 0;
            const yuzde = enBuyukAy > 0 ? (t / enBuyukAy) * 100 : 0;
            return (
              <div
                key={m}
                className={'kg-sutun' + (m === ay ? ' secili' : '')}
                title={AY_ADLARI[m - 1] + ': ' + paraFormat(t)}
              >
                <span className="kg-sutun-deger">{kisaPara(t)}</span>
                <div className="kg-sutun-cubuk" style={{ height: Math.max(yuzde, t > 0 ? 3 : 0) + '%' }} />
                <span className="kg-sutun-ay">{AY_ADLARI[m - 1].slice(0, 3)}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---- Kredi penceresi ---- */}
      {krediFormu && (
        <div className="kg-perde" onClick={() => !kaydediliyor && setKrediFormu(null)}>
          <form className="kg-pencere" onClick={(e) => e.stopPropagation()} onSubmit={krediKaydet}>
            <h3>{krediFormu.id ? krediFormu.ad : 'Yeni Kredi'}</h3>
            <p className="kg-pencere-donem">Taksitler her ay kendiliğinden düşer</p>

            <label>
              Kredinin adı
              <input
                type="text"
                className="form-control"
                value={krediFormu.ad}
                onChange={(e) => setKrediFormu({ ...krediFormu, ad: e.target.value })}
                placeholder="Örn: Garanti Taşıt Kredisi"
                autoFocus={!krediFormu.id}
              />
            </label>

            <div className="kg-ikili">
              <label>
                Kredi tutarı (₺)
                <input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  className="form-control"
                  value={krediFormu.toplamTutar}
                  onChange={(e) => setKrediFormu({ ...krediFormu, toplamTutar: e.target.value })}
                />
              </label>
              <label>
                Taksit sayısı
                <input
                  type="number"
                  min="1"
                  max="360"
                  inputMode="numeric"
                  className="form-control"
                  value={krediFormu.taksitSayisi}
                  onChange={(e) => setKrediFormu({ ...krediFormu, taksitSayisi: e.target.value })}
                />
              </label>
            </div>

            {/* Hesaplanan taksit gosterilir; banka taksidi farkliysa elle yazilabilir. */}
            <label>
              Aylık taksit (₺) <span className="kg-istege-bagli">(boş bırakırsanız hesaplanır)</span>
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                className="form-control"
                value={krediFormu.taksitTutari}
                onChange={(e) => setKrediFormu({ ...krediFormu, taksitTutari: e.target.value })}
                placeholder={(() => {
                  const t = parseFloat(String(krediFormu.toplamTutar).replace(',', '.'));
                  const n = parseInt(krediFormu.taksitSayisi, 10);
                  return t > 0 && n >= 1 ? paraFormat(Math.round((t / n) * 100) / 100) : '';
                })()}
              />
            </label>

            <div className="kg-ikili">
              <label>
                İlk taksit ayı
                <select
                  className="form-control"
                  value={krediFormu.baslangicAy}
                  onChange={(e) => setKrediFormu({ ...krediFormu, baslangicAy: Number(e.target.value) })}
                >
                  {AY_ADLARI.map((a, i) => (
                    <option key={a} value={i + 1}>{a}</option>
                  ))}
                </select>
              </label>
              <label>
                Yıl
                <input
                  type="number"
                  min="2000"
                  max="2100"
                  inputMode="numeric"
                  className="form-control"
                  value={krediFormu.baslangicYil}
                  onChange={(e) => setKrediFormu({ ...krediFormu, baslangicYil: e.target.value })}
                />
              </label>
            </div>

            <label>
              Açıklama <span className="kg-istege-bagli">(isteğe bağlı)</span>
              <input
                type="text"
                className="form-control"
                value={krediFormu.aciklama}
                onChange={(e) => setKrediFormu({ ...krediFormu, aciklama: e.target.value })}
                placeholder="Örn: 2024 model araç"
              />
            </label>

            {hata && <p className="kg-pencere-hata">{hata}</p>}

            <div className="kg-pencere-dugmeler">
              {krediFormu.id && (
                <button type="button" className="btn btn-danger btn-sm" onClick={krediSil} disabled={kaydediliyor}>
                  Sil
                </button>
              )}
              <span className="kg-bosluk" />
              <button type="button" className="btn btn-secondary" onClick={() => setKrediFormu(null)} disabled={kaydediliyor}>
                Vazgeç
              </button>
              <button type="submit" className="btn btn-primary" disabled={kaydediliyor}>
                {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---- Giriş penceresi ---- */}
      {duzenlenen && (
        <div className="kg-perde" onClick={() => !kaydediliyor && setDuzenlenen(null)}>
          <form className="kg-pencere" onClick={(e) => e.stopPropagation()} onSubmit={kaydet}>
            <h3>{duzenlenen.baslik}</h3>
            <p className="kg-pencere-donem">{AY_ADLARI[ay - 1]} {yil}</p>

            {duzenlenen.serbest && !duzenlenen.hizli && (
              <label>
                Harcamanın adı
                <input
                  type="text"
                  className="form-control"
                  value={duzenlenen.kategori}
                  onChange={(e) => setDuzenlenen({ ...duzenlenen, kategori: e.target.value })}
                  placeholder="Örn: Araç bakımı"
                  autoFocus
                />
              </label>
            )}

            <label>
              Tutar (₺)
              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                className="form-control"
                value={duzenlenen.tutar}
                onChange={(e) => setDuzenlenen({ ...duzenlenen, tutar: e.target.value })}
                autoFocus={!duzenlenen.serbest || duzenlenen.hizli}
              />
            </label>

            <label>
              Açıklama <span className="kg-istege-bagli">(isteğe bağlı)</span>
              <input
                type="text"
                className="form-control"
                value={duzenlenen.aciklama}
                onChange={(e) => setDuzenlenen({ ...duzenlenen, aciklama: e.target.value })}
                placeholder="Örn: Eylül faturası"
              />
            </label>

            {duzenlenen.odendi && (
              <label>
                Tarih
                <input
                  type="date"
                  className="form-control"
                  value={duzenlenen.tarih || ''}
                  onChange={(e) => setDuzenlenen({ ...duzenlenen, tarih: e.target.value })}
                />
              </label>
            )}

            <label className="kg-onay">
              <input
                type="checkbox"
                checked={duzenlenen.odendi}
                onChange={(e) => setDuzenlenen({ ...duzenlenen, odendi: e.target.checked })}
              />
              Ödendi
              <span className="kg-onay-not">
                İşaretlemezseniz "Bekleyen" toplamına eklenir.
              </span>
            </label>

            {hata && <p className="kg-pencere-hata">{hata}</p>}

            <div className="kg-pencere-dugmeler">
              {duzenlenen.id && (
                <button type="button" className="btn btn-danger btn-sm" onClick={sil} disabled={kaydediliyor}>
                  Sil
                </button>
              )}
              <span className="kg-bosluk" />
              <button type="button" className="btn btn-secondary" onClick={() => setDuzenlenen(null)} disabled={kaydediliyor}>
                Vazgeç
              </button>
              <button type="submit" className="btn btn-primary" disabled={kaydediliyor}>
                {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default KisiselGiderler;
